import { useMemo } from 'react'

import {
  DataCollectionPaginatedFetchOptions,
  FiltersDefinition,
  GroupingDefinition,
  ItemActionsDefinition,
  NavigationFiltersDefinition,
  SortingsDefinition,
  SummariesDefinition,
  useDataCollectionSource,
} from '@factorialco/f0-react/dist/experimental'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import { getMarketplaceGraphqlClient } from 'modules/it_management/lib/marketplaceGraphqlClient'
import { LIST_ORDERS } from 'modules/it_management/lib/marketplaceOrderGraphql'

export type OrderRecord = {
  id: string
  status: string
  currency: string
  price: string
  cartId: string
  createdAt: string | null
  itemsCount: number
  entity?: string
  type?: string
}

type OrdersFilterValues = {
  entity?: string[]
  status?: string[]
  type?: string[]
}

const PER_PAGE = 20

function toStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined
  return value.every((item) => typeof item === 'string') ? value : undefined
}

function applyFiltersAndSearch(
  orders: OrderRecord[],
  filters: OrdersFilterValues | undefined,
  search: string | undefined
): OrderRecord[] {
  let list = orders
  if (search?.trim()) {
    const q = search.trim().toLowerCase()
    list = list.filter(
      (o) =>
        o.id.toLowerCase().includes(q) ||
        o.status.toLowerCase().includes(q) ||
        o.cartId.toLowerCase().includes(q)
    )
  }
  if (filters?.status?.length && !filters.status.includes('all')) {
    const set = new Set(filters.status)
    list = list.filter((o) => set.has(o.status))
  }
  if (filters?.entity?.length && !filters.entity.includes('all')) {
    const set = new Set(filters.entity)
    list = list.filter((o) => o.entity !== undefined && set.has(o.entity))
  }
  if (filters?.type?.length && !filters.type.includes('all')) {
    const set = new Set(filters.type)
    list = list.filter((o) => o.type !== undefined && set.has(o.type))
  }
  return list
}

function applySortings(
  orders: OrderRecord[],
  sortings: { field: string; order: 'asc' | 'desc' }[] | undefined
): OrderRecord[] {
  const firstSorting = sortings?.[0]
  if (!firstSorting) return orders
  const { field, order } = firstSorting
  const cmp = order === 'asc' ? 1 : -1
  return [...orders].sort((a, b) => {
    const aVal: unknown = Reflect.get(a, field)
    const bVal: unknown = Reflect.get(b, field)
    if (aVal == null && bVal == null) return 0
    if (aVal == null) return cmp
    if (bVal == null) return -cmp
    if (typeof aVal === 'string' && typeof bVal === 'string') return cmp * aVal.localeCompare(bVal)
    if (typeof aVal === 'number' && typeof bVal === 'number') return cmp * (aVal - bVal)
    return 0
  })
}

export function useOrdersDataCollectionSource() {
  const { company } = useCurrentSession()
  const marketplaceClient = getMarketplaceGraphqlClient()
  const companyId = Number(company.id)

  const source = useDataCollectionSource<
    OrderRecord,
    FiltersDefinition<string>,
    SortingsDefinition,
    SummariesDefinition,
    ItemActionsDefinition<OrderRecord>,
    NavigationFiltersDefinition<string>,
    GroupingDefinition<OrderRecord>
  >(
    useMemo(
      () => ({
        dataAdapter: {
          paginationType: 'pages' as const,
          perPage: PER_PAGE,
          fetchData: (
            options: DataCollectionPaginatedFetchOptions<
              FiltersDefinition<string>,
              NavigationFiltersDefinition<string>
            >
          ) => {
            const { filters, sortings, search, pagination } = options
            const currentPage = pagination?.currentPage ?? 1
            const perPage = pagination?.perPage ?? PER_PAGE
            const normalizedFilters: OrdersFilterValues | undefined = filters
              ? {
                  entity: toStringArray(filters.entity),
                  status: toStringArray(filters.status),
                  type: toStringArray(filters.type),
                }
              : undefined

            return marketplaceClient
              .watchQuery({
                query: LIST_ORDERS,
                variables: { companyId },
                fetchPolicy: 'network-only',
                notifyOnNetworkStatusChange: false,
              })
              .map(({ data, loading, error }) => {
                const orders: OrderRecord[] = data?.orders ?? []
                const filtered = applyFiltersAndSearch(orders, normalizedFilters, search)
                const sorted = applySortings(filtered, sortings)
                const total = sorted.length
                const start = (currentPage - 1) * perPage
                const records = sorted.slice(start, start + perPage)

                return {
                  loading,
                  error: error ?? undefined,
                  data: {
                    records,
                    total,
                    type: 'pages' as const,
                    currentPage,
                    perPage,
                    pagesCount: Math.ceil(total / perPage) || 1,
                  },
                }
              })
          },
        },
        search: { enabled: true },
        filters: {
          entity: {
            type: 'in' as const,
            label: i18n.t('it_management.device_catalog.orders.filter_entities'),
            options: {
              options: [
                {
                  label: i18n.t('it_management.device_catalog.orders.filter_entities'),
                  value: 'all',
                },
              ],
            },
          },
          status: {
            type: 'in' as const,
            label: i18n.t('it_management.device_catalog.orders.filter_statuses'),
            options: {
              options: [
                {
                  label: i18n.t('it_management.device_catalog.orders.filter_statuses'),
                  value: 'all',
                },
                { label: 'pending', value: 'pending' },
                { label: 'authorized', value: 'authorized' },
                { label: 'paid', value: 'paid' },
                { label: 'refunded', value: 'refunded' },
                { label: 'partially_refunded', value: 'partially_refunded' },
                { label: 'cancelled', value: 'cancelled' },
              ],
            },
          },
          type: {
            type: 'in' as const,
            label: i18n.t('it_management.device_catalog.orders.filter_types'),
            options: {
              options: [
                { label: i18n.t('it_management.device_catalog.orders.filter_types'), value: 'all' },
              ],
            },
          },
        },
        sortings: {
          createdAt: {
            label: i18n.t('it_management.device_catalog.orders.column_date'),
            sortBy: 'createdAt',
          },
          status: {
            label: i18n.t('it_management.device_catalog.orders.column_status'),
            sortBy: 'status',
          },
          itemsCount: {
            label: i18n.t('it_management.device_catalog.orders.column_items'),
            sortBy: 'itemsCount',
          },
          price: {
            label: i18n.t('it_management.device_catalog.orders.column_total'),
            sortBy: 'price',
          },
        },
        defaultSortings: { field: 'createdAt' as const, order: 'desc' as const },
      }),
      [companyId, marketplaceClient]
    ),
    [companyId, marketplaceClient]
  )

  return source
}
